import { KeyboardEvent, useCallback } from 'react';

// Символы клавиш английской (US) раскладки по физической клавише: без Shift и с Shift.
// Буквы сюда не входят — их регистр задают Shift и Caps Lock вместе
const ENGLISH_LAYOUT_SYMBOLS: Record<string, readonly [string, string]> = {
  Backquote: ['`', '~'],
  Digit1: ['1', '!'],
  Digit2: ['2', '@'],
  Digit3: ['3', '#'],
  Digit4: ['4', '$'],
  Digit5: ['5', '%'],
  Digit6: ['6', '^'],
  Digit7: ['7', '&'],
  Digit8: ['8', '*'],
  Digit9: ['9', '('],
  Digit0: ['0', ')'],
  Minus: ['-', '_'],
  Equal: ['=', '+'],
  BracketLeft: ['[', '{'],
  BracketRight: [']', '}'],
  Backslash: ['\\', '|'],
  Semicolon: [';', ':'],
  Quote: ["'", '"'],
  Comma: [',', '<'],
  Period: ['.', '>'],
  Slash: ['/', '?'],
};

const LETTER_KEY_CODE = /^Key([A-Z])$/;

/** Символ английской раскладки нажатой клавиши; `null` — клавиша не вводит символа или не известна. */
const getEnglishLayoutCharacter = (event: KeyboardEvent<HTMLInputElement>): string | null => {
  const letter = LETTER_KEY_CODE.exec(event.code)?.[1];
  if (letter) {
    const isUpperCase = event.shiftKey !== event.getModifierState('CapsLock');
    return isUpperCase ? letter : letter.toLowerCase();
  }
  const symbols = ENGLISH_LAYOUT_SYMBOLS[event.code];
  return symbols ? symbols[event.shiftKey ? 1 : 0] : null;
};

/**
 * Обработчики поля ввода, в котором символы набираются в английской раскладке при любой системной:
 * нажатая клавиша вводит свой символ английской раскладки (по физической клавише, с учётом Shift и
 * Caps Lock) вместо символа системной раскладки. Сочетания с Ctrl, Alt и Cmd, ввод через систему
 * набора (IME), вставка, автозаполнение и клавиатура, не сообщающая кода клавиши, не меняются.
 */
export const useEnglishLayoutInput = () => {
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    const isImeInput = event.nativeEvent.isComposing || event.key === 'Process';
    if (event.ctrlKey || event.altKey || event.metaKey || isImeInput) {
      return;
    }
    const character = getEnglishLayoutCharacter(event);
    // Символ системной раскладки совпал с английским — вводить его штатно
    if (character === null || character === event.key) {
      return;
    }

    event.preventDefault();
    const input = event.currentTarget;
    const end = input.selectionEnd ?? input.value.length;
    input.setRangeText(character, input.selectionStart ?? end, end, 'end');
    // Правка значения мимо его свойства не видна полю формы, пока не придёт событие ввода
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  return { onKeyDown };
};
